import 'regenerator-runtime';
import styles from '@modules/modules/CookieConsent/CookieConsent.module.scss';
import useCookieConsent from '@modules/modules/CookieConsent/components/Context';
import ConsentGroup from '@modules/modules/CookieConsent/components/ConsentGroup';
import transformServices from '@modules/modules/CookieConsent/hooks/transformServices';
import Button from '@ui/components/Button/Button';
import Content from '@ui/components/Content/Content';
import Link from '@ui/components/Link/Link';
import Modal from '@ui/components/Modal/Modal';
import React, { useEffect, useRef, useState } from 'react';
import {
  useDisclosureStore,
  useFormStore,
  Form,
  Disclosure,
  DisclosureContent,
  VisuallyHidden,
} from '@ariakit/react';

const CookieConsent = () => {
  const {
    finalFocusRef,
    onAcceptAll,
    categories,
    hideOverlay,
    dialog,
    onAccept,
    dataService,
  } = useCookieConsent();
  const [values, setValues] = useState();
  const form = useFormStore({ values, setValues });
  const disclosure = useDisclosureStore();
  const contentRef = useRef(null);
  const initialFocusRef = useRef(null);
  const { mounted, open } = disclosure.useState();
  const modalOpen = dialog.useState('open');

  useEffect(() => {
    if (categories.length > 0) {
      setValues(transformServices(categories));
    }
  }, [categories]);

  useEffect(() => {
    if (mounted && contentRef.current) {
      contentRef.current.focus();
    }
  }, [mounted]);

  useEffect(() => {
    if (modalOpen) {
      setTimeout(() => {
        if (initialFocusRef.current) {
          initialFocusRef.current.focus();
        }
      }, 1);
      if (window.ppms) {
        window.ppms.cm.api('trackMainFormView');
      }
    }
  }, [modalOpen]);

  if (!dataService) {
    return null;
  }

  return (
    <Modal
      actions={
        <div className={styles.legaActionHeader}>
          <Link
            className={styles.headerLink}
            target="_blank"
            to="https://wienenergie.at/impressum"
          >
            Impressum
          </Link>
          <Link
            className={styles.headerLink}
            target="_blank"
            to="https://wienenergie.at/datenschutz"
          >
            Datenschutzerklärung
          </Link>
        </div>
      }
      className={styles.container}
      contentClassName={styles.modal}
      finalFocusRef={finalFocusRef}
      header="Datenschutz"
      hideOnClickOutside={hideOverlay}
      size="lg"
      store={dialog}
      tabIndex={0}
    >
      <div className={styles.content}>
        <div className={styles.legalActions}>
          <Link
            className={styles.headerLink}
            target="_blank"
            to="https://wienenergie.at/impressum"
          >
            Impressum
          </Link>
          <Link
            className={styles.headerLink}
            target="_blank"
            to="https://wienenergie.at/datenschutz"
          >
            Datenschutzerklärung
          </Link>
        </div>
        <VisuallyHidden>
          <div>
            Bitte treffen Sie zunächst Ihre Auswahl zu den Cookies, die wir
            setzen dürfen.
          </div>
        </VisuallyHidden>
        <Content
          className={styles.introContent}
          ref={initialFocusRef}
          tabIndex={0}
        >
          Wir, die Wien Energie GmbH und Wien Energie Vertrieb GmbH & Co KG
          nutzen diverse{' '}
          <Link
            tabIndex={-1}
            target="_blank"
            to="https://www.wienenergie.at/datenschutz/#datenschutzerklaerung-wien-energie"
          >
            Technologien
          </Link>
          , um den Betrieb unserer Website zu ermöglichen. Ebenso würden wir
          gerne mit externen Diensten personenbezogene Daten verarbeiten.{' '}
          <span className={styles.introContentDisclosureText}>
            Dies ist für die Nutzung der Website nicht notwendig, ermöglicht uns
            aber eine noch engere Interaktion mit unseren Website-Besuchern.
            Falls gewünscht, bitte eine Auswahl treffen:
          </span>
          <DisclosureContent
            ref={contentRef}
            render={<span />}
            store={disclosure}
            tabIndex={-1}
          >
            Dies ist für die Nutzung der Website nicht notwendig, ermöglicht uns
            aber eine noch engere Interaktion mit unseren Website-Besuchern.
            Falls gewünscht, bitte eine Auswahl treffen:
          </DisclosureContent>
        </Content>

        <div className={styles.introContentToggle}>
          <Disclosure
            render={
              <Button theme="secondary" variant="link">
                {open ? 'Weniger ' : 'Mehr '}Anzeigen
              </Button>
            }
            store={disclosure}
          />
        </div>
        <div>
          <Form store={form}>
            {categories.map(({ id, description, title, services }) => (
              <ConsentGroup
                description={description}
                id={id}
                isGroupVisible={id === dataService.visibleGroup}
                key={id}
                services={services}
                store={form}
                title={title}
              />
            ))}
          </Form>
        </div>
      </div>
      <div className={styles.actions}>
        <Button
          className={styles.saveAction}
          isResponsive
          onClick={() => {
            const acceptedServices = Object.keys(values).flatMap(categoryId =>
              Object.keys(values[categoryId])
                .filter(serviceId => values[categoryId][serviceId])
                .map(serviceId => serviceId),
            );
            onAccept(acceptedServices);
          }}
          variant="outline"
        >
          Auswahl akzeptieren
        </Button>
        <Button
          className={styles.saveAction}
          isResponsive
          onClick={() => {
            form.setValues(transformServices(categories, true));
            onAcceptAll();
          }}
        >
          Alle akzeptieren
        </Button>
      </div>
    </Modal>
  );
};

export default CookieConsent;
